'use client'

import { cn } from '@/lib/utils'
import { AllPurposeLink } from '@components/all-purpose-link'
import { DismissOutsideModal } from '@components/dismiss-outside-modal'
import { SOCIAL_LINKS } from '@consts/common'
import { ArrowRightIcon, DisplayText } from 'design-configuration/components'
import { AnimatePresence, motion, Variants } from 'motion/react'
import React from 'react'
import { NAV_ITEMS, NavItem } from '../constants'
import { TrackedButtonNames } from '@utils/analytics'
import { trackButtonClicked } from '@utils/analytics'

type ResponsiveMenuProps = {
  onDismiss: () => void
  visible: boolean
}

export const ResponsiveMenu = ({ onDismiss, visible }: ResponsiveMenuProps): React.ReactElement => {
  const [activeSubmenu, setActiveSubmenu] = React.useState<NavItem[] | null>(null)
  // This will lag one render behind and allow us to control the main menu animation
  // based on whether we're initially viewing the menu or if we're returning from a submenu
  const [previousActiveSubmenu, setPreviousActiveSubmenu] = React.useState<NavItem[] | null>(null)
  React.useEffect(() => {
    setPreviousActiveSubmenu(activeSubmenu)
  }, [activeSubmenu])

  const responsiveMenuItemClasses = 'gap-sm p-md flex w-full items-center font-medium'

  const mainMenuAnimationVariants: Variants = {
    initial: (previousActiveSubmenu: NavItem[] | null | undefined) => {
      if (previousActiveSubmenu) {
        return {
          x: '-100%',
          opacity: 0
        }
      }

      return {
        x: 0,
        opacity: 1
      }
    },
    animate: {
      x: 0,
      opacity: 1
    },
    exit: {
      x: '-100%',
      opacity: 0
    }
  }

  return (
    <DismissOutsideModal
      visible={visible}
      onDismiss={onDismiss}
      containerClassName="flex flex-1 flex-col h-full overflow-x-hidden relative"
    >
      <div className="relative flex-1">
        <AnimatePresence>
          {!activeSubmenu ? (
            <motion.div
              key="main-menu"
              className="absolute inset-0 flex flex-col items-start overflow-auto [justify-content:safe_center]"
              variants={mainMenuAnimationVariants}
              custom={previousActiveSubmenu}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{
                visualDuration: 0.5,
                bounce: 0
              }}
            >
              {NAV_ITEMS.map(({ label, value, subItems, href }) => {
                // Special case discord, because we show all social icons
                // below the main menu items
                if (value === 'discord') {
                  return null
                }

                if (!subItems) {
                  return (
                    <DisplayText asChild key={value}>
                      <AllPurposeLink
                        href={href ?? ''}
                        className={responsiveMenuItemClasses}
                        onClick={() => {
                          onDismiss()
                          trackButtonClicked(TrackedButtonNames.navItem, {
                            customProperties: {
                              'Nav Item': label
                            }
                          })
                        }}
                      >
                        {label}
                      </AllPurposeLink>
                    </DisplayText>
                  )
                }

                return (
                  <DisplayText asChild key={value}>
                    <button
                      className={responsiveMenuItemClasses}
                      onClick={() => {
                        setActiveSubmenu(subItems)
                      }}
                    >
                      <span>{label}</span>
                      <ArrowRightIcon className="w-md h-md fill-on-surface" />
                    </button>
                  </DisplayText>
                )
              })}
            </motion.div>
          ) : (
            <motion.div
              key="submenu"
              className="absolute inset-0 flex flex-col items-start overflow-auto [justify-content:safe_center]"
              initial={{
                x: '100%',
                opacity: 0
              }}
              animate={{
                x: 0,
                opacity: 1
              }}
              exit={{
                x: '100%',
                opacity: 0
              }}
              transition={{
                visualDuration: 0.4,
                bounce: 0
              }}
            >
              <DisplayText asChild>
                <button
                  className={cn(responsiveMenuItemClasses, 'text-on-surface-variant')}
                  onClick={() => {
                    setActiveSubmenu(null)
                  }}
                >
                  <ArrowRightIcon className="w-md h-md fill-on-surface-variant rotate-180" />
                  <span>Back</span>
                </button>
              </DisplayText>
              {activeSubmenu?.map(({ label, value, href }) => {
                return (
                  <DisplayText asChild key={value}>
                    <AllPurposeLink
                      href={href ?? ''}
                      className={responsiveMenuItemClasses}
                      onClick={() => {
                        trackButtonClicked(TrackedButtonNames.navItem, {
                          customProperties: {
                            'Nav Item': label
                          }
                        })
                        onDismiss()
                      }}
                    >
                      {label}
                    </AllPurposeLink>
                  </DisplayText>
                )
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <motion.div layout className="gap-x-md p-2xl flex flex-row items-center justify-center">
        {SOCIAL_LINKS.map(({ icon: Icon, href }) => (
          <SocialLink href={href} key={href}>
            <Icon className="fill-on-surface h-md w-md" />
          </SocialLink>
        ))}
      </motion.div>
    </DismissOutsideModal>
  )
}

const SocialLink = ({ href, children }: { href: string; children: React.ReactNode }) => {
  return (
    <a
      href={href}
      target="_blank"
      rel="norefferer"
      onClick={() => {
        trackButtonClicked(TrackedButtonNames.navItem, {
          customProperties: {
            'Nav Item': 'Social Link',
            'Social Link': href
          }
        })
      }}
    >
      {children}
    </a>
  )
}
